import { useRouter } from "next/router";
import { useCallback } from "react";

import { APIPaths } from "@/apis/api-config";
import { AuthApi } from "@/apis/connect/auth-api";
import { PartnerApi } from "@/apis/connect/partner-api";
import { useApiConfig } from "@/custom-hooks/apis-common/use-api-config";
import { useApiConnector } from "@/custom-hooks/apis-common/use-api-connector";
import { useAppSnackbar } from "@/custom-hooks/use-app-snackbar";
import { isIncompletePath } from "@/utils/common-utils";

import { AUTH_INDEX_PATH } from "@/constants/app-path";
// import { firebaseAuth } from "@/utils/firebase-utils";
import { REPORT_INDEX_PATH } from "@/constants/app-path";
import { LocalForageKeys } from "@/constants/local-forage-keys";
import { currentPartnerAtom } from "@/global-states/api-atoms";
// import { authTokenInfoAtom, fbUserCredentialAtom, loadingAtom } from "@/global-states/common-atoms";
import { authTokenInfoAtom, loadingAtom } from "@/global-states/common-atoms";
// import { signInWithCustomToken, signOut } from "@firebase/auth";
import { useAtom, useSetAtom } from "jotai";
import localforage from "localforage";

export const useAuth = () => {
  const router = useRouter();
  const { setAppSnackbar } = useAppSnackbar();
  const { appAuthApi, appPartnerApi } = useApiConnector();
  const { resetAuth, catchApiError, getRefreshToken } = useApiConfig();

  const setLoading = useSetAtom(loadingAtom);
  const [currentPartner, setCurrentPartner] = useAtom(currentPartnerAtom);
  const [authTokenInfo, setAuthTokenInfo] = useAtom(authTokenInfoAtom);

  // const setFbUserCredential = useSetAtom(fbUserCredentialAtom);

  const authSync = useCallback(
    async (opts?: { silent?: boolean }) => {
      if (isIncompletePath(router.asPath)) {
        return;
      }
      if (!opts?.silent) {
        setLoading(true);
      }
      try {
        const refreshToken = await getRefreshToken();
        if (!refreshToken) {
          await resetAuth();
          return false;
        }
        const refreshAuthApi = new AuthApi(refreshToken);
        const resultRefresh = await refreshAuthApi.postRenewToken();
        setAuthTokenInfo(resultRefresh);
        if (!opts?.silent) {
          const appPartnerApi = new PartnerApi(resultRefresh.access_token);
          const resultPartner = await appPartnerApi.getPartnerMe();
          setCurrentPartner(resultPartner.object);
        }
        return true;
      } catch (err) {
        catchApiError(err);
        await resetAuth();
        return false;
      } finally {
        if (!opts?.silent) {
          setLoading(false);
        }
      }
    },
    [authTokenInfo?.access_token, router.asPath],
  );

  //   const fbAuthSync = async (fbCustomToken?: string) => {
  //     if (!fbCustomToken) {
  //       try {
  //         setFbUserCredential(undefined);
  //         await signOut(firebaseAuth);
  //       } catch (e) {
  //         console.warn("### failed signOut for firebase. ", e);
  //       }
  //     } else {
  //       try {
  //         const userCredential = await signInWithCustomToken(firebaseAuth, fbCustomToken);
  //         setFbUserCredential(userCredential);
  //       } catch (e) {
  //         console.error("### failed signInWithCustomToken for firebase. ", e);
  //       }
  //     }
  //   };

  const authSignUpEmail = useCallback(
    async (
      requestBody: APIPaths["/partner-app/auth/signup-with-email"]["post"]["requestBody"]["content"]["application/json"],
    ) => {
      setLoading(true);
      try {
        const resultSignUp = await appAuthApi.postSignupWithEmail(requestBody);
        setAuthTokenInfo(resultSignUp);
        await localforage.setItem(LocalForageKeys.auth.AccessToken, resultSignUp.access_token);
        await localforage.setItem(LocalForageKeys.auth.RefreshToken, resultSignUp.refresh_token);
        const appPartnerApi = new PartnerApi(resultSignUp.access_token);
        const resultPartner = await appPartnerApi.getPartnerMe();
        setCurrentPartner(resultPartner.object);
        setAppSnackbar("新規登録に成功しました");
        return true;
      } catch (err) {
        catchApiError(err);
        await resetAuth();
        return false;
      } finally {
        setLoading(false);
      }
    },
    [appAuthApi],
  );

  const authLoginWithEmail = useCallback(
    async (
      requestBody: APIPaths["/partner-app/auth/login-with-email"]["post"]["requestBody"]["content"]["application/json"],
    ) => {
      setLoading(true);
      try {
        const resultLogin = await appAuthApi.postLoginWithEmail(requestBody);
        setAuthTokenInfo(resultLogin);
        await localforage.setItem(LocalForageKeys.auth.AccessToken, resultLogin.access_token);
        await localforage.setItem(LocalForageKeys.auth.RefreshToken, resultLogin.refresh_token);
        const appPartnerApi = new PartnerApi(resultLogin.access_token);
        const resultPartner = await appPartnerApi.getPartnerMe();
        setCurrentPartner(resultPartner.object);
        setAppSnackbar("ログインに成功しました", {
          autoCloseMSec: 10000,
        });
        return await router.push(REPORT_INDEX_PATH);
      } catch (err) {
        catchApiError(err);
        await resetAuth();
      } finally {
        setLoading(false);
      }
    },
    [appAuthApi],
  );

  const authLogout = useCallback(async () => {
    setLoading(true);
    try {
      await router.push(AUTH_INDEX_PATH);
      await resetAuth();
      return setAppSnackbar("ログアウトしました");
    } catch (err) {
      catchApiError(err);
    } finally {
      setLoading(false);
    }
  }, [appAuthApi]);

  const syncPartnerMe = useCallback(async () => {
    if (isIncompletePath(router.asPath)) {
      return;
    }

    setLoading(true);
    try {
      const result = await appPartnerApi.getPartnerMe();
      setCurrentPartner(result.object);
      return true;
    } catch (err) {
      catchApiError(err);
      return false;
    } finally {
      setLoading(false);
    }
  }, [appPartnerApi, router.asPath]);

  return {
    authSync: authSync,
    authSignUpEmail: authSignUpEmail,
    authLoginWithEmail: authLoginWithEmail,
    authLogout: authLogout,
    syncPartnerMe: syncPartnerMe,
  };
};
